import { useCallback, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import {
  IGeneratePaycometCheckoutLinkMutation,
  useGeneratePaycometCheckoutLinkMutation,
} from 'generated/rbi-graphql';
import { popupCenter } from 'utils/center-popup';
import { isNative } from 'utils/environment';
import logger from 'utils/logger';

import { OutcomeResult } from './types';

interface IPaycometPaypalPopupResult {
  openPaycometPaypalPopup: () => void;
  isLoadingPopup: boolean;
  isPopupOpen: boolean;
}

const webInitialPopupStyles = `
  position: absolute;
  top: 50%;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  font: bold 16px Helvetica, sans-serif;
  width: 100%;
  text-align:center;
`;

export const usePaycometPaypalPopup = (
  onResult: (outcome: OutcomeResult) => void,
  serverOrder: IServerOrder | null
): IPaycometPaypalPopupResult => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const webOpenedWindowRef: any = useRef(undefined);

  const { formatMessage } = useIntl();

  const openPreWebPopup = useCallback(() => {
    webOpenedWindowRef.current = popupCenter({ url: '', width: 440, height: 599 });

    if (webOpenedWindowRef.current) {
      webOpenedWindowRef.current.document.write(
        `<div style="${webInitialPopupStyles}">${formatMessage({ id: 'loadingPaypal' })}</div>`
      );
      webOpenedWindowRef.current.document.title = formatMessage({
        id: 'paycometPaypalPopup',
      });

      setIsPopupOpen(true);
    }
  }, [formatMessage]);

  const openWebModal = useCallback(
    (url: any): void => {
      webOpenedWindowRef.current.location.href = url;
      const win = webOpenedWindowRef.current;

      const listener = (e: MessageEvent) => {
        const origin = new URL(e.origin);

        if (e.data === 'payment-paypal-closed') {
          setIsPopupOpen(false);
          return;
        }

        if (!origin.hostname.endsWith('.rbictg.com')) {
          return;
        }

        if (e.data.type !== 'payment-outcome') {
          return;
        }

        win?.close();

        const body = e.data.body as OutcomeResult;

        onResult(body);

        window.removeEventListener('message', listener);
      };

      window.addEventListener('message', listener);
    },
    [onResult]
  );

  const openNativeModal = useCallback(
    (url: any): void => {
      if (!window.cordova) {
        throw new Error('"window.cordova" is not defined');
      }
      if (!(window.cordova as any).InAppBrowser) {
        throw new Error('"window.cordova.InAppBrowser" is not defined');
      }

      const InAppBrowser = (window.cordova as any).InAppBrowser;

      const win = InAppBrowser.open(url, '_blank', 'location=yes');

      if (win) {
        setIsPopupOpen(true);
      }

      win.addEventListener('message', (e: any) => {
        if (e.data.type !== 'payment-outcome') {
          return;
        }
        win.close();

        const body = e.data.body as OutcomeResult;

        onResult(body);
      });
    },
    [onResult]
  );

  const handlePaycometLinkCompleted = useCallback(
    (result: IGeneratePaycometCheckoutLinkMutation) => {
      if (!result.generatePaycometCheckoutLink.iframeUrl) {
        throw new Error(`Is missing iframeUrl`);
      }

      if (isNative) {
        openNativeModal(result.generatePaycometCheckoutLink.iframeUrl);
      } else {
        openWebModal(result.generatePaycometCheckoutLink.iframeUrl);
      }
    },
    [openNativeModal, openWebModal]
  );

  const [generatePaycometLink, { loading: isLoadingPopup }] =
    useGeneratePaycometCheckoutLinkMutation({
      onCompleted: handlePaycometLinkCompleted,
      onError: (error: any) => {
        if (!isNative) {
          webOpenedWindowRef.current.document.close();
          webOpenedWindowRef.current.document.write(
            `<div style="${webInitialPopupStyles}">
          ${formatMessage({ id: 'errorLoadingPaypal' })}
          <br/>
          ${formatMessage({ id: 'pleaseTryPaypalAgain' })}
          </div>`
          );
          webOpenedWindowRef.current.document.title = formatMessage({
            id: 'paycometPaypalPopup',
          });
        }

        logger.error({
          message: 'Paycomet: Non-vaulted Paypal integration threw an error',
          error,
        });

        throw error;
      },
    });

  const openPaycometPaypalPopup = useCallback((): void => {
    if (!isNative) {
      openPreWebPopup();
    }

    if (serverOrder && serverOrder.rbiOrderId) {
      generatePaycometLink({
        variables: { rbiOrderId: serverOrder.rbiOrderId, typePayment: 'PAYPAL' },
      });
    }
  }, [generatePaycometLink, openPreWebPopup, serverOrder]);

  return {
    openPaycometPaypalPopup,
    isLoadingPopup,
    isPopupOpen,
  };
};

export default usePaycometPaypalPopup;
